<template>
  <div :class="$style.errorOverlay" data-test="restage-error-overlay">
    <HgNotificationMessage
      :title="t('imageGallery.restage.error.title')"
      theme="warning"
      :showCloseButton="true"
      :class="$style.notificationMessage"
      :showCtaButton="true"
      data-test="restage-error-notification"
      @close="onClose"
    >
      <p>{{ t('imageGallery.restage.error.message') }}</p>
      <template #ctaButton>
        <HgButton
          theme="secondary-bordered"
          data-test="restage-try-again-button"
          :class="$style.tryAgainButton"
          @click="onTryAgain"
        >
          {{ t('imageGallery.restage.button.tryAgain') }}
        </HgButton>
      </template>
    </HgNotificationMessage>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { HgButton, HgNotificationMessage } from '@smg-real-estate/domus';

import { useRestageStore } from '@/store/modules/restage';
import type { HgImage } from '@/store/types';
import { get } from 'lodash-es';

const { t } = useI18n();

const props = defineProps<{
  currentImage: HgImage;
}>();

const restageStore = useRestageStore();

const transformation = computed(() =>
  get(restageStore.transformationsByImageUrl, [props.currentImage.src]),
);

const onClose = (): void => {
  restageStore.resetImageState(props.currentImage.src);
};

const onTryAgain = (): void => {
  if (transformation.value) {
    restageStore.restageImage(props.currentImage.src, transformation.value);
  }
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.errorOverlay {
  position: absolute;
  inset: 0;
  width: 360px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  @media (max-width: 480px) {
    top: 40%;
  }
}

.notificationMessage {
  @media (max-width: 480px) {
    h4 {
      @include heading-5;
    }

    max-width: 320px;
    height: 180px;
  }

  p {
    margin: 0;

    @media (max-width: 480px) {
      @include body-3;
    }
  }
}

.tryAgainButton {
  @include body-3;
}
</style>
